import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { AlertCircle, Mail, ArrowLeft, CheckCircle, Send } from 'lucide-react'

type Step = 'email' | 'sent'

export default function Login() {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { requestCode } = useAuth()

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await requestCode(email)
      if (result.success) {
        setStep('sent')
      } else {
        setError(result.error || 'Failed to send magic link')
      }
    } catch (err) {
      setError('Failed to send magic link. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    setStep('email')
    setError('')
  }

  const handleResend = async () => {
    setError('')
    setLoading(true)

    try {
      const result = await requestCode(email)
      if (result.success) {
        // Show brief confirmation
        setError('')
      } else {
        setError(result.error || 'Failed to resend')
      }
    } catch (err) {
      setError('Failed to resend. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-700 via-primary-700 to-primary-600 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-20 w-20 mb-4">
            <img src="/logos/clarantis-icon.svg" alt="Clarantis" className="w-20 h-20" />
          </div>
          <h1 className="text-3xl font-bold text-white">Clarantis</h1>
          <p className="text-gray-400 mt-2">Admin Console</p>
        </div>

        <div className="card p-8">
          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="text-center mb-4">
                <Mail className="h-12 w-12 text-primary-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-white">Sign in with email</h2>
                <p className="text-gray-400 text-sm mt-1">
                  We'll send you a magic link to sign in
                </p>
              </div>

              <div>
                <label htmlFor="email" className="label">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="admin@company.com"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? (
                  <>
                    <Send className="h-4 w-4 mr-2 animate-pulse" />
                    Sending...
                  </>
                ) : (
                  'Send Magic Link'
                )}
              </button>
            </form>
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-gray-400 hover:text-white text-sm transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Use different email
              </button>

              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-600/20 mb-4">
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
                <h2 className="text-lg font-semibold text-white">Check your email</h2>
                <p className="text-gray-400 text-sm mt-2">
                  We sent a magic link to
                </p>
                <p className="text-primary-400 font-medium mt-1">{email}</p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-400">
                <p className="mb-2">
                  <strong className="text-white">Click the link in your email</strong> to continue signing in.
                </p>
                <p>
                  You'll be asked to enter a verification code shown in the email.
                </p>
              </div>

              <div className="text-center pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-500 mb-2">Didn't receive the email?</p>
                <button
                  onClick={handleResend}
                  disabled={loading}
                  className="text-sm text-primary-400 hover:text-primary-300 disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Resend magic link'}
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-700">
            <p className="text-xs text-gray-500 text-center">
              Clarantis Admin Console for platform management.
              <br />
              Access is restricted to platform administrators.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
